import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import DownloadInstagramContent from './DownloadInstagramContent';
import ChooseContent from './ChooseContent';
import WhyDownloadStoriesContent from './WhyDownloadStoriesContent';
import FAQContent from './FAQContent';
import BestTipsContent from '../LandingPage/BestTipsContent';
import DownloadStoriesBottomContent from '../PostComments/DownloadStoriesBottomContent';
import AlterModal from './AlterModal';
import CommentsPlan from './CommentsPlan';
import { fetchGetExportNumber, fetchInsFollowerOrFollowingList, fetchOrderSubsListRequest } from '@/lib/service/home';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import { message } from 'antd';
import { roll } from '@/icons/iconSvg';
import { fetchInsMonitorSearch } from '@/lib/service';
import { InsAccountModel } from '@/types/home';
import { isInsUserUrl } from '@/lib/utils';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import dayjs from 'dayjs';
import useFollowExportPremium from '@/lib/hooks/isPremium/useFollowExportPremium';
import { useRouter } from 'next/router';

const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const UserDashboard = dynamic(() => import('../Dashboard/UserDashboard'), { ssr: false });
const InstagramUserInfo = dynamic(() => import('./InstagramUserInfo'), { ssr: false });
const DataPanel = dynamic(() => import('./DataPanel'), { ssr: false });

export default function IgFollowerExport({ data }: any) {
  const contentData = data?.fetchDataRes;
  const { t } = useTranslation('common');
  const [searchStr, setSearchStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUserPlan, setCurrentUserPlan] = useState<any>(null);
  const { isLogin, guideToRegisterOrLogin } = useRegisterOrLoginGuide();
  const [currentInsUser, setCurrentInsUser] = useState<InsAccountModel>(null);
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const { isPremium } = useFollowExportPremium();
  const onValueChanged = (e?: string) => {
    setSearchStr(e);
  };

  const router = useRouter();

  // 是否是可以导出
  const isUse = useMemo(() => {
    if (!currentUserPlan?.expire_time) return false;
    if (dayjs().isAfter(dayjs(currentUserPlan?.expire_time))) {
      return false;
    }
    return true;
  }, [currentUserPlan?.expire_time]);

  const [type, setType] = useState<'following' | 'followers'>('followers');

  useEffect(() => {
    if (!router.isReady) return;
    const { username, relation } = router.query;
    if (username && typeof username === 'string') {
      setSearchStr(username);
    }
    if (relation && (relation === 'followers' || relation === 'following')) {
      setType(relation);
    }
  }, [router.isReady, router.query]);

  const onSearch = async (e?: string, isView: boolean = false) => {
    if (!e) return;
    if (!isLogin) {
      guideToRegisterOrLogin(() => {
        console.log(e);
      });
      return;
    }
    if (isSearchLoading || loading) {
      return;
    }

    setLoading(true);
    const res = await fetchInsMonitorSearch(e, undefined, { bizFuncName: 'follow_export', isPremium });
    setLoading(false);
    if (res.code == 0) {
      const data = {
        is_private: res?.data[0]?.is_private,
        media_name: res?.data[0]?.media_name,
        profile_pic_url: res?.data[0]?.profile_pic_url,
      };
      if (data?.is_private == 1) {
        setOpenPrivateAccount({
          title: t('OopsPrivateProfileDetected'),
          desc: (
            <>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
              <UserDashboard {...data} />
            </>
          ),
          btnTitle: t('TRYAGAIN'),
          btnDesc: (
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          ),
        });
      } else if (data?.is_private == 2) {
        setOpenPrivateAccount({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
        });
      } else {
        // is_private == 0 正常逻辑
        setCurrentInsUser(res.data[0]);
        if (!isUse && !isView) {
          handleOpenErrorAlterModal(res.data[0]);
          return;
        }
        implamentFunc(e, isUse ? 5000 : 500);
      }
    } else {
      message.error(res.message);
    }
  };

  const [allItems, setAllItems] = useState<any[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [openErrorAlterModal, setOpenErrorAlterModal] = useState<any>(null);
  const handleOpenErrorAlterModal = (data?: InsAccountModel) => {
    setOpenErrorAlterModal({
      title: t('Warning'),
      desc: t(
        `Your free plan limits you to exporting only 500 xxx1 at a time, This account has xxx2 xxx3. Would you like to upgrade your plan to export more data?`
      )
        .replace('xxx1', type == 'followers' ? 'followers' : 'following')
        .replace(
          'xxx2',
          type == 'followers' ? data?.follower_count.toLocaleString() : data?.following_count.toLocaleString()
        )
        .replace('xxx3', type == 'followers' ? 'followers' : 'following'),
      btnTitle: t('Upgrade Plan'),
      type: true,
      cancelBtnTitle: t('No, thanks'),
    });
  };

  const implamentFunc = async (e: string, maxCount: number) => {
    let collectedItems: any[] = [];
    let pageNo = 1;
    let hasNext = true;
    setIsSearchLoading(true);

    while (collectedItems.length < maxCount && hasNext) {
      const res = await fetchInsFollowerOrFollowingList(type, e, pageNo, { bizFuncName: 'follow_export', isPremium });
      if (res.code == 0) {
        if (res?.data?.items) {
          collectedItems = collectedItems.concat(res.data.items);
        }
        setAllItems(collectedItems);
        hasNext = res?.data?.has_next;
        pageNo += 1;
        if (!hasNext) {
          setIsSearchLoading(false);
          break;
        }
      } else if (res.code == 23002) {
        // 无数据
        message.error(res.message);
        setIsSearchLoading(false);
        break;
      } else if (res.code == 23001) {
        //需要支付
        message.error(res.message);
        setIsSearchLoading(false);
        break;
      } else if (res.code == 23003 || res.code == 23004) {
        message.error(t('Export limit reached. You can’t export more data.'));
        setCurrentInsUser(null);
        setIsSearchLoading(false);
        break;
      } else {
        message.error(res.message);
        setIsSearchLoading(false);
        //返回上一操作页面
        setCurrentInsUser(null);
        setAllItems([]);
        setSearchStr('');
        numberRef.current = 0;
        break;
      }
    }
    // fetchRecordExportData(e, type, collectedItems.length);
    setIsSearchLoading(false);
    if (collectedItems.length > maxCount) {
      collectedItems = collectedItems.slice(0, maxCount);
      setAllItems(collectedItems);
    }
  };

  const numberRef = useRef<number>(0);
  const [exportNumber, setExportNumber] = useState<number>(0);
  const refreshNumber = async () => {
    const res = await fetchGetExportNumber();
    if (res.code == 0) {
      numberRef.current = res.data;
      setExportNumber(res.data);
    }
  };

  const rightStr = useRef<string>('');

  useEffect(() => {
    if (isLogin) {
      fetchOrderSubsListRequest('follow_export', data.locale).then((res: any) => {
        setCurrentUserPlan(res.data?.length > 0 ? res.data[res.data.length - 1] : null);
      });
      refreshNumber();
    } else {
      setCurrentUserPlan(null);
    }
  }, [isLogin]);

  return (
    <div>
      <div className="max-w-[1200px] mx-auto px-2 md:px-0 mb-10">
        <div>
          <TopSearchContent
            topInfo={contentData?.contentInfo?.hero}
            loading={loading}
            searchVal={searchStr}
            currentInsUser={currentInsUser}
            onSearchClick={(e) => {
              const searchStr = e.trim();
              if (searchStr.includes('/reel/') || searchStr.includes('/reels/')) {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
              const content = isInsUserUrl(searchStr);
              rightStr.current = content;
              setAllItems([]);
              if (content) {
                onSearch(content);
              } else {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
            }}
            onValueChanged={onValueChanged}
            inputPlaceholder={t('Enter an Instagram username or profile link')}
            middleContent={
              isLogin ? <SubContent currentUserPlan={currentUserPlan} t={t} exportNumber={exportNumber} /> : <></>
            }
            setType={setType}
            type={type}
          />
        </div>
        {currentInsUser && (
          <>
            <InstagramUserInfo currentInsUser={currentInsUser} />
            {allItems.length > 0 && (
              <div className="border max-w-[1000px] mt-10 shadow-lg rounded-lg mx-auto bg-white">
                {isSearchLoading ? (
                  <div className="h-[200px] w-full flex flex-col items-center justify-center">
                    <div className="font-bold text-xl">{t('Your file is nearly complete...')}</div>
                    <div className="w-[300px] border h-[40px] border-gray-300 rounded-md mt-6 flex items-center justify-center relative">
                      <div className="flex gap-2 items-center z-10">
                        <span className="animate-spin">{roll}</span>
                        <span className="text-gray-500 text-sm">{t('Exported Loading...')}</span>
                        <span className="text-gray-500 text-sm">
                          {allItems.length}/{isUse ? '5000' : '500'}
                        </span>
                      </div>
                      <div
                        className="absolute top-0 left-0 h-[38px] bg-[#F1EFFF] rounded-md transition-all duration-300  flex items-center justify-center"
                        style={{
                          width: `${(allItems.length / (isUse ? 5000 : 500)) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <DataPanel
                    searchStr={searchStr}
                    type={type}
                    currentInsUser={currentInsUser}
                    exportNumber={exportNumber}
                    allItems={allItems}
                    isUse={isUse}
                    currentUserPlan={currentUserPlan}
                    refreshNumber={refreshNumber}
                    handleBack={() => {
                      setCurrentInsUser(null);
                      setAllItems([]);
                      setSearchStr('');
                      numberRef.current = 0;
                      rightStr.current = '';
                    }}
                  />
                )}
              </div>
            )}
          </>
        )}

        <DownloadInstagramContent data={contentData?.contentInfo?.['section-1']} />
        <ChooseContent data={contentData?.contentInfo?.['section-2']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo?.['section-4']} />
        <CommentsPlan
          currentUserPlan={currentUserPlan}
          guideToRegisterOrLogin={guideToRegisterOrLogin}
          isLogin={isLogin}
        />
        <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </div>
      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        placeholder={t('Enter an Instagram username or profile link')}
        buttonTitle={t('Search Now')}
        loading={false}
        searchVal={searchStr}
        inputVisible={true}
        onValueChanged={(e) => {
          setSearchStr(e);
        }}
        onSearchClick={(e) => {
          document.scrollingElement?.scrollTo({ top: 0, behavior: 'smooth' });
          if (loading || isSearchLoading) return;
          const searchStr = e.trim();
          const content = isInsUserUrl(searchStr);
          rightStr.current = content;
          setAllItems([]);
          if (content) {
            onSearch(content);
          } else {
            setOpenPrivateAccount({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
            });
            return;
          }
        }}
      />

      {/*输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={(isOk: boolean) => {
          setOpenPrivateAccount(null);
          if (isOk) {
          }
        }}
      />

      {/* 限制弹窗 */}
      <AlterModal
        visible={openErrorAlterModal !== null}
        title={openErrorAlterModal?.title}
        type={openErrorAlterModal?.type}
        btnTitle={openErrorAlterModal?.btnTitle}
        cancelBtnTitle={openErrorAlterModal?.cancelBtnTitle}
        desc={openErrorAlterModal?.desc}
        onCancel={() => {
          setOpenErrorAlterModal(null);
          onSearch(rightStr.current, true);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          setOpenErrorAlterModal(null);
          setCurrentInsUser(null);
          const PricingTitle = document.querySelector('#pricing-title');
          if (PricingTitle) {
            PricingTitle.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
}

const SubContent = ({
  currentUserPlan,
  t,
  exportNumber,
}: {
  currentUserPlan: any;
  t: (s: string) => string;
  exportNumber: number;
}) => {
  if (currentUserPlan) return <></>;

  return (
    <div className="flex gap-4 lg:w-[520px] w-full flex-wrap mx-auto  justify-center text-sm">
      <div className="flex gap-2">
        <span className="font-bold">{t('Export Quota')}:</span>
        <span className="text-green-500">{3000 - exportNumber}</span>
      </div>
      <div className="flex gap-2">
        <span className="font-bold">{t('Export Limit')}:</span>
        <span className="text-green-500">500</span>
      </div>
      <div className="flex gap-2">
        <span className="font-bold">{t('Current Plan')}:</span>
        <span className="text-green-500 font-bold">Free</span>
        <span
          className="text-[#725CFF] hover:underline font-bold cursor-pointer"
          onClick={() => {
            const PricingTitle = document.querySelector('#pricing-title');
            if (PricingTitle) {
              PricingTitle.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          {t('Upgrade Now')}
        </span>
      </div>
    </div>
  );
};
